import * as EthAddress from 'ethers/address'

import { isSolanaAddress } from '@root/entities/network/model/solana/solana-address'
import { TonUtil } from '@root/shared/lib/ton'

import type { ImportWalletTrackerReq } from '../atoms/wallet-tracker-requests'

/**
 * Reading a tracked-wallet list somebody exported from somewhere else.
 *
 * The importer used to `JSON.parse` the file and post it, which works for a
 * list this terminal wrote and quietly sends nonsense for anything else: the
 * server sees no `walletAddress`, no `network`, and rejects the lot with one
 * message that says only "wrong format".
 *
 * Other terminals name the same things differently and keep settings this one
 * has nowhere to put — per-alert toggles, sounds, a creation date. Those are
 * dropped on purpose. What carries over is the wallet, what it is called, its
 * emoji, the groups it was in, and whether it was alerting at all.
 */

/**
 * The limits the API declares, in `ImportTrackingWalletsSchema`. Longer values
 * are cut rather than refused: the server validates the whole array at once,
 * so one over-long group name would cost the other hundred and seventy-eight
 * wallets a 400 with nothing to say which entry was at fault.
 *
 * A tag is shorter than a label — 32 against 64 — and they are not the same
 * limit however alike they read.
 */
const MAX_LABEL = 64
const MAX_TAG = 32
const MAX_EMOJI = 16
const MAX_TAGS = 16
const MAX_ADDRESS = 128

/**
 * A list as this terminal exports it.
 *
 * Recognised by the pair of fields the others do not have: the address under
 * its own name, and a network, which no other format carries because each of
 * them only ever served one chain.
 */
type NativeWallet = {
  walletAddress?: unknown
  network?: unknown
  label?: unknown
  tags?: unknown
  emoji?: unknown
  alertsEnabled?: unknown
}

/**
 * A list as the trackers built around alert toggles export it — the shape of
 * the file this was written for, 179 wallets across two groups.
 *
 * There is no network on it. Every one of those terminals is Solana-only, so
 * the chain has to be worked out from the address itself.
 */
type ForeignWallet = {
  trackedWalletAddress?: unknown
  name?: unknown
  emoji?: unknown
  groupNames?: unknown
  alertsOnToast?: unknown
  alertsOnBubble?: unknown
  alertsOnFeed?: unknown
  alertsOnTransfer?: unknown
}

export type ImportOutcome = {
  wallets: Array<ImportWalletTrackerReq>
  /** Entries that carried no address this app can use, and were left out. */
  skipped: number
}

const text = (value: unknown, limit: number): string =>
  typeof value === 'string' ? value.trim().slice(0, limit) : ''

const flag = (value: unknown): boolean => value === true

type SupportedNetwork = ImportWalletTrackerReq['network']

const NETWORKS: ReadonlyArray<SupportedNetwork> = [
  'sol',
  'eth',
  'base',
  'bsc',
  'ton',
]

/**
 * The chain an address belongs to.
 *
 * A format with no network field has to be read off the address itself. The
 * three checks are the same ones the network entity uses, imported from the
 * modules that hold them rather than through `@root/entities/network` — that
 * barrel pulls a cycle which leaves `Network` undefined under vitest, and it
 * is what breaks ten other suites in this project.
 *
 * An EVM address is valid on all three EVM chains, so `eth` is what an
 * address with no other evidence becomes; a file that meant Base or BSC has
 * to say so in its own `network` field.
 */
const networkOf = (address: string): SupportedNetwork | undefined => {
  if (isSolanaAddress(address)) {
    return 'sol'
  }
  if (EthAddress.isAddress(address)) {
    return 'eth'
  }
  if (TonUtil.isAddressValid(address)) {
    return 'ton'
  }
  return undefined
}

const tagsOf = (value: unknown): Array<string> => {
  if (!Array.isArray(value)) {
    return []
  }
  const seen = new Set<string>()
  for (const item of value) {
    const tag = text(item, MAX_TAG)
    if (tag) {
      seen.add(tag)
    }
  }
  return [...seen].slice(0, MAX_TAGS)
}

const isForeign = (entry: object): entry is ForeignWallet =>
  'trackedWalletAddress' in entry

const fromNative = (
  entry: NativeWallet,
): ImportWalletTrackerReq | undefined => {
  const walletAddress = text(entry.walletAddress, MAX_ADDRESS)
  if (!walletAddress) {
    return undefined
  }
  // Its own network is taken at face value when it is one we know, and worked
  // out from the address when it is not — an older export may predate a chain
  // being renamed.
  const declared = NETWORKS.find((network) => network === entry.network)
  const network = declared ?? networkOf(walletAddress)
  if (!network) {
    return undefined
  }
  return {
    walletAddress,
    network,
    label: text(entry.label, MAX_LABEL),
    tags: tagsOf(entry.tags),
    emoji: text(entry.emoji, MAX_EMOJI),
    alertsEnabled: entry.alertsEnabled !== false,
  }
}

const fromForeign = (
  entry: ForeignWallet,
): ImportWalletTrackerReq | undefined => {
  const walletAddress = text(entry.trackedWalletAddress, MAX_ADDRESS)
  if (!walletAddress) {
    return undefined
  }
  const network = networkOf(walletAddress)
  if (!network) {
    return undefined
  }
  return {
    walletAddress,
    network,
    label: text(entry.name, MAX_LABEL),
    // Their groups are our tags: both are a flat list of names a wallet
    // belongs to, and both are what the list is filtered by.
    tags: tagsOf(entry.groupNames),
    emoji: text(entry.emoji, MAX_EMOJI),
    // They keep a switch per surface — a toast, a bubble, the feed, transfers.
    // This app has one. A wallet that was alerting anywhere still alerts.
    alertsEnabled:
      flag(entry.alertsOnToast) ||
      flag(entry.alertsOnBubble) ||
      flag(entry.alertsOnFeed) ||
      flag(entry.alertsOnTransfer),
  }
}

/**
 * Turns a parsed file into wallets this app can post, whichever terminal
 * wrote it.
 *
 * An entry that cannot be read is counted and stepped over, not thrown: a
 * single bad line in a list of two hundred should not cost the other hundred
 * and ninety-nine. `undefined` means the file is not a list of wallets at all,
 * which is the one case worth refusing outright.
 */
export const readImportedWallets = (
  parsed: unknown,
): ImportOutcome | undefined => {
  if (!Array.isArray(parsed)) {
    return undefined
  }

  const wallets: Array<ImportWalletTrackerReq> = []
  const byAddress = new Set<string>()
  let skipped = 0

  for (const entry of parsed) {
    if (typeof entry !== 'object' || entry === null) {
      skipped += 1
      continue
    }
    const wallet = isForeign(entry)
      ? fromForeign(entry)
      : fromNative(entry as NativeWallet)

    if (!wallet) {
      skipped += 1
      continue
    }
    // The same wallet twice is one wallet. A file stitched together from two
    // exports has plenty of those, and the server would answer with a
    // conflict for every one.
    const key = `${wallet.network}:${wallet.walletAddress}`
    if (byAddress.has(key)) {
      continue
    }
    byAddress.add(key)
    wallets.push(wallet)
  }

  return { wallets, skipped }
}
